import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

// ─── IMPORT HERO IMAGE ────────────────────────────────────────────────────────
import fitneseqp5 from '../../assets/images/fitneseqp5.png';

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
  viewport: { once: true },
});

const PHONE = '+254117110000';
const EMAIL = 'powercoreltd26@gmail.com';

// Updated Google Maps link (same as footer)
const GOOGLE_MAPS_LINK = 'https://share.google/kWFfHwv3VOOGAPOzR';

const Contact = () => {
  const [form, setForm] = useState({ name: '', phone: '', message: '' });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleWhatsApp = (e) => {
    e.preventDefault();
    if (!form.name || !form.message) return;
    const text = encodeURIComponent(
      `Hi PowerCore! 👋\n\nName: ${form.name}\nPhone: ${form.phone || 'N/A'}\n\nMessage:\n${form.message}`
    );
    window.open(`https://wa.me/${PHONE.replace(/\+/g, '')}?text=${text}`, '_blank');
  };

  const handleEmail = () => {
    window.location.href = `mailto:${EMAIL}?subject=Enquiry from ${form.name || 'Website Visitor'}&body=${encodeURIComponent(form.message || '')}`;
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');
        input::placeholder, textarea::placeholder { color: #bbb; }
        input:focus, textarea:focus { outline: none; border-color: #800020 !important; box-shadow: 0 0 0 3px rgba(128,0,32,0.1); }
      `}</style>

      {/* ── 1. HERO 300px with fitneseqp5 and no overflow color ── */}
      <section className="relative w-full overflow-hidden" style={{ height: '300px' }}>
        <img src={fitneseqp5} alt="Contact PowerCore" className="absolute inset-0 w-full h-full object-cover object-center" />
        
        {/* Clean dark overlay — no maroon tint */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0.3) 100%)' }} />
        
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        
        <div className="absolute inset-0 flex flex-col justify-center">
          <div className="container-custom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fade(0.1)} className="max-w-xl">
              <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-full mb-3 border border-white/30 text-white/70">
                Get In Touch
              </span>
              <h1 className="font-black text-white leading-none mb-2"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2rem, 5vw, 3.5rem)', letterSpacing: '-0.02em' }}>
                WE'D LOVE TO <span style={{ color: '#e8a0b0' }}>HEAR FROM YOU</span>
              </h1>
              <p className="text-white/60 text-sm">Reach out via WhatsApp, email or visit us at Southfield Mall.</p>
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-12" style={{ background: 'linear-gradient(to top, white, transparent)' }} />
      </section>

      {/* ── 2. CONTACT INFO STRIP - UPDATED with correct Google Maps link ── */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="container-custom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: <PhoneIcon className="h-5 w-5" />,
                label: 'WhatsApp & Call',
                value: PHONE,
                sub: 'Mon-Fri: 4AM-10PM | Sat: 8AM-9PM | Sun: 8AM-8PM',
                action: () => window.open(`https://wa.me/${PHONE.replace(/\+/g, '')}`, '_blank'),
              },
              {
                icon: <EnvelopeIcon className="h-5 w-5" />,
                label: 'Email Us',
                value: EMAIL,
                sub: 'We reply within 24 hours',
                action: () => window.location.href = `mailto:${EMAIL}`,
              },
              {
                icon: <MapPinIcon className="h-5 w-5" />,
                label: 'Find Us',
                value: 'Southfield Mall',
                sub: 'Nairobi Express Highway, Nairobi',
                action: () => window.open(GOOGLE_MAPS_LINK, '_blank'),
              },
            ].map((item, i) => (
              <motion.button key={i} {...fade(i * 0.1)}
                onClick={item.action}
                className="flex items-start gap-4 p-5 rounded-2xl border border-gray-100 hover:border-red-100 hover:shadow-md transition-all text-left group w-full">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110"
                  style={{ background: 'rgba(128,0,32,0.08)', color: '#800020' }}>
                  {item.icon}
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">{item.label}</p>
                  <p className="font-bold text-gray-900 text-sm">{item.value}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{item.sub}</p>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. FORM + MAP - UPDATED with correct Google Maps embed ── */}
      <section className="py-14 bg-white">
        <div className="container-custom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10">

            {/* Contact Form */}
            <motion.div {...fade(0)}>
              <span className="text-xs font-bold tracking-[0.2em] uppercase mb-2 inline-block" style={{ color: '#800020' }}>Send a Message</span>
              <h2 className="font-black text-gray-900 mb-6"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}>
                CHAT WITH US ON WHATSAPP
              </h2>

              <form onSubmit={handleWhatsApp} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">Your Name *</label>
                  <input
                    type="text" name="name" value={form.name} onChange={handleChange} required
                    placeholder="e.g. Jane Wanjiku"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 transition-all bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">Phone Number</label>
                  <input
                    type="tel" name="phone" value={form.phone} onChange={handleChange}
                    placeholder="+254 7XX XXX XXX"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 transition-all bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">Message *</label>
                  <textarea
                    name="message" value={form.message} onChange={handleChange} required rows={4}
                    placeholder="Tell us how we can help — membership queries, facility tours, personal training..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 transition-all bg-gray-50 resize-none"
                  />
                </div>

                {/* WhatsApp submit */}
                <button type="submit"
                  className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.02] hover:shadow-lg"
                  style={{ background: '#25D366', boxShadow: '0 4px 20px rgba(37,211,102,0.3)' }}>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Send via WhatsApp
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-100" />
                  <span className="text-xs text-gray-400">or</span>
                  <div className="flex-1 h-px bg-gray-100" />
                </div>

                {/* Email CTA */}
                <button type="button" onClick={handleEmail}
                  className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-sm transition-all hover:scale-[1.02] border-2 border-gray-200 hover:border-red-200 text-gray-700 hover:text-gray-900">
                  <EnvelopeIcon className="h-5 w-5" style={{ color: '#800020' }} />
                  Send via Email Instead
                </button>
              </form>
            </motion.div>

            {/* Map - UPDATED with correct Google Maps embed link */}
            <motion.div {...fade(0.15)} className="flex flex-col gap-4">
              <div>
                <span className="text-xs font-bold tracking-[0.2em] uppercase mb-2 inline-block" style={{ color: '#800020' }}>Where To Find Us</span>
                <h2 className="font-black text-gray-900 mb-1"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}>
                  SOUTHFIELD MALL
                </h2>
                <p className="text-gray-500 text-sm">Along Nairobi Express Highway, Nairobi, Kenya</p>
              </div>

              {/* Embedded Google Map - using updated share link */}
              <div className="relative rounded-2xl overflow-hidden shadow-lg border border-gray-100 flex-1" style={{ minHeight: '340px' }}>
                <iframe
                  title="PowerCore Fitness Location - Southfield Mall"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.8559!2d36.889239!3d-1.329231!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f126cba09e533%3A0xc931e36004533ba7!2sSouthfield%20Mall!5e1!3m2!1sen!2ske!4v1710000000000!5m2!1sen!2ske"
                  width="100%"
                  height="100%"
                  style={{ border: 0, position: 'absolute', inset: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              {/* Quick links below map - UPDATED with correct Google Maps link */}
              <div className="grid grid-cols-2 gap-3">
                <a href={`https://wa.me/${PHONE.replace(/\+/g, '')}`} target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-105"
                  style={{ background: '#25D366' }}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp Us
                </a>
                <a href={GOOGLE_MAPS_LINK} target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-105"
                  style={{ background: '#800020' }}>
                  <MapPinIcon className="h-4 w-4" />
                  Get Directions
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 4. HOURS & QUICK INFO ── */}
      <section className="py-10" style={{ background: '#faf8f8' }}>
        <div className="container-custom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Opening Hours */}
            <motion.div {...fade(0)} className="bg-white rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(128,0,32,0.08)', color: '#800020' }}>
                  <ClockIcon className="h-5 w-5" />
                </div>
                <h4 className="font-bold text-gray-900 text-sm">Opening Hours</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Monday – Friday</span>
                  <span className="font-semibold text-gray-800">4:00 AM – 10:00 PM</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Saturday</span>
                  <span className="font-semibold text-gray-800">8:00 AM – 9:00 PM</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Sunday & Holidays</span>
                  <span className="font-semibold text-gray-800">8:00 AM – 8:00 PM</span>
                </div>
              </div>
            </motion.div>

            {/* Class Schedule */}
            <motion.div {...fade(0.1)} className="bg-white rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(128,0,32,0.08)', color: '#800020' }}>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6l4 2" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
                <h4 className="font-bold text-gray-900 text-sm">Class Schedule</h4>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">Classes (Mon – Fri)</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Morning Session</span>
                      <span className="font-semibold text-gray-800">6:00 AM – 7:00 AM</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Evening Session</span>
                      <span className="font-semibold text-gray-800">6:00 PM – 7:00 PM</span>
                    </div>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-400">Saturday Class</p>
                  <p className="text-xs font-semibold text-gray-800">8:00 AM – 9:00 AM</p>
                </div>
                <div className="pt-1">
                  <p className="text-xs text-gray-400 mt-1">📍 Dedicated PCF Studio for all classes</p>
                </div>
              </div>
            </motion.div>

            {/* Contact Details */}
            <motion.div {...fade(0.2)} className="bg-white rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(128,0,32,0.08)', color: '#800020' }}>
                  <PhoneIcon className="h-5 w-5" />
                </div>
                <h4 className="font-bold text-gray-900 text-sm">Contact Details</h4>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">WhatsApp & Phone</p>
                  <a href={`tel:${PHONE}`} className="font-bold text-sm hover:underline" style={{ color: '#800020' }}>{PHONE}</a>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Email</p>
                  <a href={`mailto:${EMAIL}`} className="font-bold text-sm hover:underline break-all" style={{ color: '#800020' }}>{EMAIL}</a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 5. READY TO JOIN CTA ── */}
      <section className="py-14 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #800020 0%, #3d0010 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl" />
        </div>
        <div className="container-custom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div {...fade(0)}>
            <span className="text-xs font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full inline-block mb-4 bg-white/20 text-white/80">
              Limited Spots Available
            </span>
            <h2 className="font-black text-white mb-4 leading-none"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
              READY TO START YOUR <span className="text-yellow-400">JOURNEY?</span>
            </h2>
            <p className="text-white/75 mb-8 max-w-lg mx-auto text-sm leading-relaxed">
              Join PowerCore today and transform your life. No contracts. No excuses. Just results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-black text-sm text-gray-900 bg-white transition-all hover:scale-105 hover:shadow-2xl">
                View Pricing & Join <ArrowRightIcon className="h-4 w-4" />
              </Link>
              <a href={`https://wa.me/${PHONE.replace(/\+/g, '')}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-bold text-sm text-white border-2 border-white/40 hover:bg-white/10 transition-all">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Contact;